import { useCallback, useState } from 'react';
import { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { parseApiError, ParsedApiError } from './apiErrorHelper';
import { TranslateFn } from './validationRules';
import ResourcesIds from '../resources/ResourcesIds';

/** What a failed request turns into: some of it on fields, the rest above the form. */
export interface ApiFormErrorOutcome {
    /** Field name to message, already filtered to fields this form actually carries. */
    fieldErrors: Record<string, string>;
    /** The form-level message, or undefined when every problem landed on a field. */
    formError?: string;
}

/**
 * Splits an API failure into what belongs on a field and what belongs above the form.
 *
 * <p>`knownFields` is the guard the hand-written ladder never had. `setError('slug', ...)`
 * on a form with no `slug` is silently dropped by react-hook-form, so a server naming a
 * field this form does not carry used to make the message *vanish* -- a request that
 * failed for no visible reason. Anything unrecognised is folded into the form-level
 * message instead, unfamiliar field name and all: ugly, and far better than silence.
 *
 * <p>Pure, so it can be tested without rendering anything.
 */
export const splitApiError = (
    error: unknown,
    knownFields: Iterable<string>,
    translate: TranslateFn,
    fallbackKey: string
): ApiFormErrorOutcome => {
    const parsed: ParsedApiError = parseApiError(error);
    const known: Set<string> = new Set(knownFields);

    const fieldErrors: Record<string, string> = {};
    const unplaceable: string[] = [];

    Object.entries(parsed.fieldErrors).forEach(([field, message]) => {
        if (known.has(field)) {
            fieldErrors[field] = message;
        } else {
            unplaceable.push(`${field}: ${message}`);
        }
    });

    if (unplaceable.length > 0) {
        return { fieldErrors, formError: unplaceable.join('; ') };
    }
    if (Object.keys(fieldErrors).length > 0) {
        return { fieldErrors };
    }
    if (parsed.message) {
        return { fieldErrors, formError: parsed.message };
    }
    if (parsed.isNetworkError) {
        return { fieldErrors, formError: translate(ResourcesIds.ERROR_NETWORK) };
    }
    if (parsed.status === 403) {
        return { fieldErrors, formError: translate(ResourcesIds.ERROR_FORBIDDEN) };
    }

    return { fieldErrors, formError: translate(fallbackKey) };
};

/**
 * The ladder every form used to write out by hand: apply what can go on a field, and hand
 * back the rest for an alert or a snackbar.
 *
 * <p>`knownFields` is normally `Object.keys(schema.fields)`, so the list maintains itself
 * as the schema changes.
 */
export const useApiFormError = <T extends FieldValues>(
    setError: UseFormSetError<T>,
    knownFields: Iterable<string>,
    translate: TranslateFn,
    fallbackKey: string
): {
    formError?: string;
    report: (error: unknown) => void;
    /** A form-level message this form raised itself, for what the schema cannot see. */
    show: (message: string) => void;
    clear: () => void;
} => {
    const [formError, setFormError] = useState<string | undefined>(undefined);

    const report = useCallback((error: unknown): void => {
        const outcome: ApiFormErrorOutcome = splitApiError(error, knownFields, translate, fallbackKey);

        Object.entries(outcome.fieldErrors).forEach(([field, message]) =>
            setError(field as Path<T>, { type: 'server', message }));

        setFormError(outcome.formError);
    }, [setError, knownFields, translate, fallbackKey]);

    const show = useCallback((message: string): void => setFormError(message), []);
    const clear = useCallback((): void => setFormError(undefined), []);

    return { formError, report, show, clear };
};
